// translations/pages/kyveil/en.js

export const kyveilPageEN = {
  "seo": {
    "title": "Kyveil PII Anonymization | pAIoneer Labs GmbH",
    "description": "Kyveil detects and anonymizes personally identifiable information locally on your systems. Desktop application or REST API for GDPR-ready PII anonymization.",
    "keywords": "Kyveil, PII anonymization, anonymize personal data, privacy, GDPR, Microsoft Presidio, spaCy, flair, local data processing",
    "ogTitle": "Kyveil: Professional PII Anonymization with AI and NLP",
    "ogDescription": "Automatic detection and anonymization of personal data as a desktop application or REST API on your own infrastructure.",
    "twitterTitle": "Kyveil PII Anonymization",
    "twitterDescription": "Local PII detection and anonymization for sensitive documents.",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "Software for local detection and anonymization of personally identifiable information with AI and NLP.",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "Privacy and anonymization",
    "title": "Kyveil",
    "subtitle": "Local anonymization before AI, tests and sharing.",
    "description": "Kyveil is a local anonymization layer for documents and AI workflows. The software detects personal data in Word, Excel, PDF and text files, replaces or masks it and shows a preview before content enters AI chats, tests, analytics or external sharing. Kyveil is developed and continuously advanced [IDD-driven](idd) (Intent-Driven Development) and automated with [Veridion](veridion) by our AI staff with humans in the loop.",
    "platforms": "Desktop application, REST API and AnonChat",
    "proofPoints": [
      "Documents stay local on your systems.",
      "Personal data is replaced or masked.",
      "A preview shows the anonymized content before sharing or AI chat.",
      "Word, Excel, PDF and text are the core document workflows."
    ]
  },
  "dataSovereignty": {
    "title": "Your Data. Your Systems. Your Control.",
    "subtitle": "Complete data sovereignty through local processing.",
    "items": [
      {
        "title": "No Cloud Required",
        "description": "All processing happens locally on your infrastructure. Your files never leave your systems. No cloud dependencies, no external data transmission."
      },
      {
        "title": "Edge Computing",
        "description": "The desktop application runs completely standalone on workstations. The API deploys on your own servers. Both versions process everything locally within your security perimeter."
      },
      {
        "title": "Data Autonomy",
        "description": "Full control over your data flows. No vendor lock-in. No recurring cloud costs. Your sensitive information remains exclusively under your control."
      },
      {
        "title": "Standard Hardware",
        "description": "No GPU required. Runs on conventional office computers and standard servers. No specialized hardware investments needed."
      }
    ]
  },
  "features": {
    "title": "Key Features",
    "subtitle": "Everything needed for controlled PII detection and anonymization in business workflows.",
    "items": [
      {
        "title": "AI-Based PII Detection",
        "description": "Automatic recognition using Microsoft Presidio SDK, spaCy by Explosion AI and flair by Humboldt University of Berlin.",
        "tone": "primary"
      },
      {
        "title": "Replace Mode",
        "description": "Substitutes detected PII with generic placeholders like [NAME], [EMAIL] and [ADDRESS] for compliance scenarios.",
        "tone": "info"
      },
      {
        "title": "Fake Mode",
        "description": "Replaces detected PII with plausible substitute values for test environments and development.",
        "tone": "warning"
      },
      {
        "title": "Multiple Document Formats",
        "description": "Processes Microsoft Word, Excel, OpenDocument, PDF files and plain text.",
        "tone": "success"
      },
      {
        "title": "Multi-Language Support",
        "description": "Built-in support for German, English, French, Italian and Spanish with language-specific NLP models.",
        "tone": "secondary"
      },
      {
        "title": "Configurable Detection",
        "description": "Define which PII entities to detect: names, addresses, emails, phone numbers, bank accounts, tax IDs and more.",
        "tone": "primary"
      },
      {
        "title": "Extensibility",
        "description": "Interfaces for custom pre-processing and post-processing logic to integrate with your specific workflows.",
        "tone": "info"
      },
      {
        "title": "CPU-Optimized",
        "description": "Efficient model architecture runs on standard office hardware without GPU requirements.",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "Technology Stack",
    "subtitle": "Enterprise-grade frameworks and proven NLP research.",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK. Industry-standard framework for PII recognition and anonymization with built-in entity recognizers.",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Developed by Explosion AI",
        "description": "Industrial-Strength Natural Language Processing. Production-ready NLP library with pre-trained statistical models for named entity recognition.",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "Developed by Humboldt University of Berlin",
        "description": "State-of-the-art NLP framework with contextual string embeddings. Advanced neural network models for improved entity detection accuracy.",
        "tone": "secondary"
      }
    ],
    "integration": "Integrated AI Pipeline",
    "integrationDesc": "Combined recognition using Presidio as orchestration layer with spaCy and flair models for optimal detection across multiple languages and entity types."
  },
  "deployment": {
    "title": "Deployment Options",
    "subtitle": "Choose the right solution for your use case.",
    "options": [
      {
        "title": "Desktop Application",
        "subtitle": "Standalone application for individual workstations",
        "description": "The desktop version runs completely independently on Windows, macOS or Linux. Ideal for users who need to anonymize documents directly at their workplace without network dependencies.",
        "features": [
          "100% offline capability - no internet connection required",
          "Files never leave the local workstation",
          "Batch processing for multiple files",
          "Graphical user interface for easy operation",
          "Runs on standard office computers without GPU",
          "One-time license - no subscription model"
        ]
      },
      {
        "title": "REST API",
        "subtitle": "Server deployment for integration and scaling",
        "description": "The API version deploys on your own servers or private cloud infrastructure. Ideal for integrating anonymization into existing systems and workflows with high processing volumes.",
        "features": [
          "Deploy on your own infrastructure - on-premise or private cloud",
          "Horizontal scaling for high throughput requirements",
          "RESTful API with comprehensive documentation",
          "Same anonymization engine as desktop version",
          "Integration examples for common platforms",
          "Support for pre- and post-processing customization"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "AI web chat with anonymization before the chat",
        "description": "AnonChat places Kyveil in front of the actual AI chat. Word and Excel documents are anonymized, a preview is reviewed and only then is content used with ChatGPT, Claude, Gemini or internal LLMs.",
        "features": [
          "Anonymization before the chat instead of after-the-fact control",
          "Preview of the anonymized text before handoff",
          "Designed for document-based questions to AI systems",
          "Reduces the risk of sending personal data to external models",
          "Can connect to internal LLM or enterprise chat setups",
          "Uses the same anonymization approach as desktop and REST API"
        ]
      }
    ],
    "bothNote": "Desktop application, REST API and AnonChat are designed to anonymize sensitive content before further processing."
  },
  "technicalDetails": {
    "title": "Technical Capabilities",
    "subtitle": "Configurable detection for European business documents.",
    "items": [
      {
        "title": "Language Support",
        "description": "Pre-trained NLP models for German, English, French, Italian and Spanish. Language-specific entity recognition for accurate PII detection across European business contexts.",
        "tone": "primary"
      },
      {
        "title": "Configurable PII Entities",
        "description": "Select which personally identifiable information types to detect and anonymize: person names, email addresses, phone numbers, postal addresses, IBAN bank accounts, credit card numbers, social security numbers, tax IDs, passport numbers and more.",
        "tone": "info"
      },
      {
        "title": "Anonymization Modes",
        "description": "Replace Mode substitutes PII with generic placeholders for compliance documentation. Fake Mode uses plausible substitute values for testing environments while maintaining document structure.",
        "tone": "warning"
      },
      {
        "title": "Pre- and Post-Processing",
        "description": "Implement custom logic before and after anonymization. Add document validation, format conversion, quality checks, audit logging or integration with downstream systems through provided interfaces.",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChat extends Kyveil with an AI web chat",
    "subtitle": "The new app uses the same anonymization approach before the actual chat with external or internal AI systems.",
    "eyebrow": "New application",
    "name": "AnonChat",
    "description": "AnonChat is an AI web chat where Word and Excel documents can be anonymized before use with ChatGPT, Claude, Gemini or internal LLMs. A preview shows the anonymized text before content is moved into the chat.",
    "statement": "The user keeps the decision point: review, approve, then chat.",
    "steps": [
      {
        "title": "Load document",
        "description": "Word or Excel files are prepared for anonymization before they enter the AI workflow.",
        "tone": "primary"
      },
      {
        "title": "Anonymize PII",
        "description": "Kyveil detects personal data and replaces or masks it according to the selected rules.",
        "tone": "info"
      },
      {
        "title": "Review preview",
        "description": "The anonymized text is visible before content is handed to the chat.",
        "tone": "warning"
      },
      {
        "title": "Use AI chat",
        "description": "Only after review is the anonymized content used with external or internal LLMs.",
        "tone": "success"
      }
    ]
  },
  "useCases": {
    "title": "Use Cases",
    "subtitle": "From GDPR compliance to safe development data.",
    "items": [
      {
        "title": "GDPR Compliance",
        "description": "Anonymize personal data before sharing documents externally or with internal departments. Meet data protection requirements for test data, analysis datasets and documentation.",
        "tone": "primary"
      },
      {
        "title": "Test Data Preparation",
        "description": "Prepare realistic test datasets so developers can work with production-like data without exposing real personal information.",
        "tone": "info"
      },
      {
        "title": "HR Document Processing",
        "description": "Anonymize applications, employee files and HR documentation for unbiased reviews, compliance audits or data analysis while protecting privacy.",
        "tone": "warning"
      },
      {
        "title": "Data Analysis",
        "description": "Enable analysis of documents containing PII by anonymizing sensitive information first. Share datasets across teams without privacy concerns.",
        "tone": "success"
      },
      {
        "title": "Document Archiving",
        "description": "Prepare documents for long-term archiving by removing or anonymizing PII according to retention policies and data protection regulations.",
        "tone": "secondary"
      },
      {
        "title": "Secure Sharing",
        "description": "Share documents with external partners, contractors or other departments after removing sensitive personal information automatically.",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "Editions and Support",
    "subtitle": "Updates, extensions and support for production use.",
    "items": [
      {
        "title": "Desktop, API and AnonChat",
        "description": "Kyveil is available as a desktop application and as a REST API for integrations. AnonChat is the web chat that anonymizes documents before the conversation.",
        "tone": "info"
      },
      {
        "title": "Support",
        "description": "For use in companies, we provide technical support, help with implementation and customization.",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Get Started with Kyveil",
    "subtitle": "Protect sensitive documents before they enter AI, tests, analytics or sharing.",
    "desktop": "Request the desktop application",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "Contact for REST API Deployment",
    "contact": "For a demo or a quote, write to us.",
    "email": "info@paioneer.eu",
    "apiSubject": "Kyveil REST API Deployment",
    "contactSubject": "Kyveil demo or quote"
  }
};
